import type { NextRouter } from 'next/router';
import { useMemo } from 'react';

import { type CardPageRouteInfo, PageModes } from '../types/types';
import { ACCEPTED_ROUTES, getPathMatch, normalisePageMode, normaliseTab } from '../util';

// All this is doing now is getting the tab from the query params, this can be done in a much simpler way, might want to deprecate this in future
// This hook is being used by the CardEditPage, which is in turn used in the template edit page, the team card edit page, and personal card edit page
export function useCardPageRouteInfo(router: NextRouter): CardPageRouteInfo {
  const cardPageRouteInfo = useMemo(
    () =>
      router.isReady // don't match the path until the router is ready
        ? // We don't want to match on query params as you have to match them exactly which isn't possible
          getPathMatch(
            router.asPath.split('?')[0],
            Object.values(ACCEPTED_ROUTES),
            (matchingRoute, values) => {
              switch (matchingRoute) {
                // template routes
                case ACCEPTED_ROUTES.ALL_TEMPLATES:
                  return {
                    pageMode: PageModes.View,
                    tab: 'card',
                    params: { id: undefined },
                  };
                case ACCEPTED_ROUTES.TEMPLATE_CARD_PREVIEW:
                  return {
                    pageMode: normalisePageMode(router.query.mode),
                    tab: normaliseTab(router.query.tab),
                    params: { id: values.id },
                  };
                case ACCEPTED_ROUTES.NEW_TEMPLATE:
                  break;
                case ACCEPTED_ROUTES.TEMPLATE_EDIT:
                case ACCEPTED_ROUTES.TEMPLATE_EDIT_DRAFT:
                  return {
                    pageMode: normalisePageMode(router.query.mode),
                    tab: normaliseTab(router.query.tab),
                    params: { id: values.id },
                  };
                // team cards
                case ACCEPTED_ROUTES.TEAM_CARD: {
                  return {
                    pageMode: normalisePageMode(router.query.mode),
                    tab: normaliseTab(router.query.tab),
                    params: { id: values.id },
                  };
                }
                case ACCEPTED_ROUTES.NEW_TEAM_CARD: {
                  const from = Array.isArray(router.query.from) ? undefined : router.query.from;

                  return {
                    pageMode: PageModes.Create,
                    tab: normaliseTab(router.query.tab),
                    params: { from, id: undefined },
                  };
                }
                case ACCEPTED_ROUTES.TEAM_CARD_EDIT:
                case ACCEPTED_ROUTES.TEAM_CARD_EDIT_REDIRECT:
                  return {
                    pageMode: normalisePageMode(router.query.mode),
                    tab: normaliseTab(router.query.tab),
                    params: { id: values.id },
                  };
                // personal cards
                case ACCEPTED_ROUTES.PERSONAL_CARD:
                  return {
                    pageMode: normalisePageMode(router.query.mode),
                    tab: normaliseTab(router.query.tab),
                    params: { id: values.id },
                  };
                case ACCEPTED_ROUTES.EDIT:
                  return {
                    pageMode: normalisePageMode(router.query.mode),
                    tab: normaliseTab(router.query.tab),
                    params: { id: values.id },
                  };
                case ACCEPTED_ROUTES.EDIT_DRAFT:
                  return {
                    pageMode: normalisePageMode(router.query.mode),
                    tab: normaliseTab(router.query.tab),
                    params: { id: values.id },
                  };
                case ACCEPTED_ROUTES.NEW_PERSONAL_CARD: {
                  const from = Array.isArray(router.query.from) ? undefined : router.query.from;

                  return {
                    pageMode: PageModes.Create,
                    tab: normaliseTab(router.query.tab),
                    params: { from, id: undefined },
                  };
                }
                default:
                  // I gave the card ID just to shut the compiler up
                  return { pageMode: 'invalid', tab: undefined, params: { id: undefined } };
              }
            },
          )
        : ({
            pageMode: 'create',
            params: { id: undefined },
            tab: 'card',
          } as CardPageRouteInfo),
    [router.asPath, router.query.from, router.query.mode, router.query.tab, router.isReady],
  );

  return cardPageRouteInfo;
}
